import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Users, 
  Music, 
  Sparkles, 
  Star, 
  Shield, 
  Search,
  Flag,
  Flame
} from 'lucide-react';
import api from '../config/api';
import HeroSection from '../components/common/HeroSection';

const TeamPage = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [logoUrl, setLogoUrl] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchTeamMembers();
    fetchLogo();
  }, []);

  const fetchLogo = async () => {
    try {
      const response = await api.get('/about-page');
      if (response.ok) {
        const data = await response.json();
        if (data.logo) {
          setLogoUrl(data.logo);
        }
      }
    } catch (error) {
      console.error('Error fetching logo:', error);
    }
  };

  const parseMemberCategories = (member) => {
    if (!member) return ['Shambhu Garjana'];
    let cats = member.categories;
    if (typeof cats === 'string') {
      try {
        cats = JSON.parse(cats);
      } catch (e) {
        if (cats.includes(',')) {
          cats = cats.split(',').map(s => s.trim()).filter(Boolean);
        } else if (cats.trim()) {
          cats = [cats.trim()];
        }
      }
    }
    if (!Array.isArray(cats) || cats.length === 0) {
      cats = member.category ? [member.category] : ['Shambhu Garjana'];
    }
    return cats;
  };

  const fetchTeamMembers = async () => {
    try {
      setLoading(true);
      const response = await api.get('team');
      if (response.ok) {
        const data = await response.json();
        const parsed = Array.isArray(data) ? data.map(m => ({
          ...m,
          categories: parseMemberCategories(m),
          category: parseMemberCategories(m)[0] || m.category || 'Shambhu Garjana'
        })) : [];
        setMembers(parsed);
      } else {
        console.error('Failed to fetch team members');
        setMembers([]);
      }
    } catch (error) {
      console.error('Error fetching team members:', error);
      setMembers([]);
    } finally {
      setLoading(false);
    }
  };

  // Default team categories to render in order if present
  const defaultCategories = [
    'Shambhu Garjana',
    'Shiv Shakti',
    'Shiv Garjana',
    'Rudra Dance',
    'ShankhNaad',
    'Folk Taal (Lezim, Dhwaj, Zaanz)',
    'Janta Raja',
    'Management'
  ];

  // Gather all unique categories present in members + default ones
  const allCategories = [
    ...new Set([
      ...defaultCategories,
      ...members.flatMap(m => Array.isArray(m.categories) && m.categories.length > 0 ? m.categories : [m.category]).filter(Boolean)
    ])
  ];

  // Category Icon Map
  const getCategoryIcon = (category) => {
    switch (category?.toLowerCase()) {
      case 'shambhu garjana':
        return <Music className="w-6 h-6 text-orange-600" />;
      case 'shiv shakti':
        return <Flame className="w-6 h-6 text-amber-600" />;
      case 'shiv garjana':
        return <Flame className="w-6 h-6 text-red-600" />;
      case 'rudra dance':
        return <Sparkles className="w-6 h-6 text-purple-600" />;
      case 'folk taal (lezim, dhwaj, zaanz)':
        return <Flag className="w-6 h-6 text-orange-600" />;
      case 'janta raja':
        return <Star className="w-6 h-6 text-amber-600" />;
      case 'management':
        return <Shield className="w-6 h-6 text-blue-600" />;
      default:
        return <Users className="w-6 h-6 text-orange-600" />;
    }
  };

  // Filter members based on search
  const filteredMembers = members.filter(member => {
    return !searchQuery ||
           member.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
           member.role?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  return (
    <>
      <Helmet>
        <title>Cultural Teams & Performers - Beats of Washington</title>
        <meta 
          name="description" 
          content="Meet the Shambhu Garjana, Shiv Shakti, Shiv Garjana, Rudra Dance, ShankhNaad, Folk Taal, and Janta Raja teams of Beats of Washington." 
        />
      </Helmet>

      {/* Hero Section */}
      <HeroSection
        title={
          <>
            <span>Our Performers</span>
            <br />
            <span>& Cultural</span>
            <br />
            <span>Teams</span>
          </>
        }
        description="Celebrate the rhythm, energy, and passion of our cultural teams."
        logoUrl={logoUrl}
        showLogo={true}
        floatingElements={[
          { icon: Music, position: 'top-12 left-16', animation: 'animate-bounce' },
          { icon: Sparkles, position: 'top-24 right-24', animation: 'animate-pulse' },
          { icon: Shield, position: 'bottom-20 left-1/4', animation: 'animate-spin-slow' },
          { icon: Star, position: 'bottom-16 right-1/3', animation: 'animate-bounce' }
        ]}
      />

      <main className="bg-gradient-to-b from-gray-50 via-white to-orange-50/20 min-h-screen py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto space-y-12">

          {/* Section Introduction */}
          <div className="text-center max-w-3xl mx-auto space-y-3">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-orange-100/80 text-orange-800 rounded-full text-xs font-extrabold uppercase tracking-wider">
              <Sparkles className="w-4 h-4 text-orange-600" />
              <span>Cultural Showcase</span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-gray-900 tracking-tight">
              Beats of Washington Teams
            </h2>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed">
              Explore our performers across Shambhu Garjana, Shiv Shakti, Shiv Garjana, Rudra Dance, ShankhNaad, Folk Taal, and Janta Raja!
            </p>
          </div>

          {/* Top Search Bar */}
          <div className="bg-white rounded-3xl p-5 shadow-xl border border-gray-100 flex justify-center items-center max-w-md mx-auto">
            <div className="relative w-full">
              <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search performer name or role..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:bg-white transition-all text-center"
              />
            </div>
          </div>

          {/* Loading State */}
          {loading ? (
            <div className="py-20 text-center">
              <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-500 font-semibold text-base">Loading team members...</p>
            </div>
          ) : filteredMembers.length === 0 ? (
            <div className="bg-white rounded-3xl p-16 text-center shadow-lg border border-gray-100 max-w-md mx-auto">
              <Users className="w-16 h-16 text-orange-300 mx-auto mb-4 animate-pulse" />
              <h3 className="text-xl font-bold text-gray-900">No Team Members Found</h3>
              <p className="text-gray-500 text-sm mt-2">
                No performers match your search or selected section filter.
              </p>
            </div>
          ) : (
            /* Categorized Team Sections */
            <div className="space-y-16">
              {allCategories.map((category) => {
                const categoryMembers = filteredMembers.filter(m => {
                  const memberCats = Array.isArray(m.categories) && m.categories.length > 0 ? m.categories : [m.category];
                  return memberCats.includes(category);
                });
                if (categoryMembers.length === 0) return null;

                return (
                  <section key={category} className="space-y-8">
                    {/* Team Header Banner */}
                    <div className="flex items-center gap-3 border-b-2 border-orange-200 pb-3">
                      <div className="p-3 bg-orange-100/80 rounded-2xl shadow-sm">
                        {getCategoryIcon(category)}
                      </div>
                      <div>
                        <h3 className="text-2xl sm:text-3xl font-black text-gray-900 tracking-tight">
                          {category} Team
                        </h3>
                        <p className="text-xs sm:text-sm font-semibold text-orange-600">
                          {categoryMembers.length} {categoryMembers.length === 1 ? 'Performer' : 'Performers'}
                        </p>
                      </div>
                    </div>

                    {/* Circular Avatar Grid for this Team */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-6 sm:gap-8">
                      {categoryMembers.map((member) => (
                        <div
                          key={member.id}
                          className="group bg-white rounded-3xl p-5 border border-gray-100 shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 flex flex-col items-center text-center relative overflow-hidden"
                        >
                          {/* Top accent line */}
                          <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-amber-400 to-orange-500 opacity-0 group-hover:opacity-100 transition-opacity" />

                          {/* Circle Avatar */}
                          <div className="w-28 h-28 sm:w-32 sm:h-32 rounded-full p-1.5 bg-gradient-to-tr from-amber-400 via-orange-500 to-red-500 shadow-lg group-hover:shadow-orange-500/30 group-hover:scale-105 transition-all duration-300 mb-4 relative flex-shrink-0">
                            {member.photo ? (
                              <img
                                src={member.photo}
                                alt={member.name}
                                className="w-full h-full rounded-full object-cover bg-white"
                              />
                            ) : (
                              <div className="w-full h-full rounded-full bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center text-white font-black text-3xl">
                                {member.name ? member.name.charAt(0) : 'T'}
                              </div>
                            )}
                          </div>

                          {/* Member Name */}
                          <h4 className="font-black text-gray-900 text-base sm:text-lg group-hover:text-orange-600 transition-colors line-clamp-1">
                            {member.name}
                          </h4>

                          {/* Optional Role if provided */}
                          {member.role && (
                            <p className="text-xs font-bold text-gray-500 mt-0.5 line-clamp-1">
                              {member.role}
                            </p>
                          )}

                        </div>
                      ))}
                    </div>
                  </section>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </>
  );
};

export default TeamPage;
